import React, { useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import { Icon } from "ui/Icon"
import FilterChips from "ui/FilterChips"
import DateRangeFilterPanel from "ui/Filters/DateRangeFilterPanel"
import CheckboxFilterPanel from "ui/Filters/CheckboxFilterPanel"
import { FILTER_PANEL_VARIANTS } from "ui/Filters/constants"
import { findMatchingPreset } from "ui/Filters/utils"
import { BLACKOUT_FILTER_SECTIONS } from "../../constants"
import { formatFilterRangeLabel, formatTargetsSummary } from "../../utils"
import FilterPill from "./FilterPill"
import FilterAccordion from "./FilterAccordion"
import styles from "./index.module.css"

/** The single value the date-range chip stands for — a range is one selection, not a list. */
const DATE_CHIP_VALUE = "date-range"

/**
 * Group options -> the flat `{ value, label }` pairs `FilterChips` matches a selection against.
 * The label is a node so a long listing name can ellipsize instead of stretching the row.
 */
const toChipOptions = (groups, labelClassName) =>
  groups
    .flatMap((group) => group.options)
    .map((option) => ({
      value: option.id,
      label: <span className={labelClassName}>{option.suffix ? `${option.label} ${option.suffix}` : option.label}</span>
    }))

/**
 * Date Range / Resources / Listings, applied by the list request rather than in the browser.
 *
 * Two shapes for one filter set: a row of dropdown pills from tablet up, and a bottom sheet of
 * collapsible sections on phones, where each collapsed section summarises its own selection.
 */
const BlackoutFilters = ({ filters, onChange, onClear, resourceGroups, listingGroups }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()

  const [openPanel, setOpenPanel] = useState(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const togglePanel = (panel) => (isOpen) => setOpenPanel(isOpen ? panel : null)
  const closePanel = () => setOpenPanel(null)

  const hasDateRange = Boolean(filters.dateRange)
  const activeCount = (hasDateRange ? 1 : 0) + (filters.resourceIds.length ? 1 : 0) + (filters.listingIds.length ? 1 : 0)

  const resourcesLabel = t("settings.blackout.filter_resources", { defaultValue: "Resources" })
  const listingsLabel = t("settings.blackout.filter_listings", { defaultValue: "Listings" })
  const dateLabel = t("settings.blackout.filter_date_range", { defaultValue: "Date Range" })

  /** A chosen range reads as its preset name when it matches one, and as its dates otherwise. */
  const matchedPreset = findMatchingPreset(filters.dateRange, dayjs().startOf("day"))
  const dateRangeText = matchedPreset
    ? t(matchedPreset.shortLabelKey, { defaultValue: matchedPreset.shortLabelDefault })
    : formatFilterRangeLabel(filters.dateRange)

  /**
   * Every applied selection also stands on its own as a removable chip: the pills and the mobile
   * trigger only *count* what is narrowing the list, so the names are otherwise behind a panel.
   * Both layouts render the same row — on a phone it sits under the Filters button, which is the
   * only place the selection is visible without reopening the sheet.
   */
  const chips = activeCount > 0 && (
    <Flex wrap gap="var(--spacing-xs)" className={styles.chips}>
      <FilterChips
        gap={8}
        selectedValues={hasDateRange ? [DATE_CHIP_VALUE] : []}
        options={[{ value: DATE_CHIP_VALUE, label: dateRangeText }]}
        onChange={() => onChange({ dateRange: null })}
      />
      <FilterChips
        gap={8}
        selectedValues={filters.resourceIds}
        options={toChipOptions(resourceGroups, styles.chipLabel)}
        onChange={(resourceIds) => onChange({ resourceIds })}
      />
      <FilterChips
        gap={8}
        selectedValues={filters.listingIds}
        options={toChipOptions(listingGroups, styles.chipLabel)}
        onChange={(listingIds) => onChange({ listingIds })}
      />
    </Flex>
  )

  if (isMobile) {
    const sections = [
      {
        key: BLACKOUT_FILTER_SECTIONS.DATE,
        label: t("settings.blackout.filter_date_range_mobile", { defaultValue: "Date range" }),
        summary: hasDateRange ? dateRangeText : t("settings.blackout.filter_date_all", { defaultValue: "Using current range" }),
        isActive: hasDateRange,
        onReset: () => onChange({ dateRange: null }),
        panel: (
          <DateRangeFilterPanel
            isCompact
            variant={FILTER_PANEL_VARIANTS.INLINE}
            value={filters.dateRange}
            onChange={(dateRange) => onChange({ dateRange })}
          />
        )
      },
      {
        key: BLACKOUT_FILTER_SECTIONS.RESOURCES,
        label: resourcesLabel,
        summary:
          formatTargetsSummary(filters.resourceIds, resourceGroups) ||
          t("settings.blackout.filter_resources_all", { defaultValue: "All resources" }),
        isActive: filters.resourceIds.length > 0,
        onReset: () => onChange({ resourceIds: [] }),
        panel: (
          <CheckboxFilterPanel
            hasFooter={false}
            variant={FILTER_PANEL_VARIANTS.INLINE}
            groups={resourceGroups}
            selectedIds={filters.resourceIds}
            onApply={(resourceIds) => onChange({ resourceIds })}
          />
        )
      },
      {
        key: BLACKOUT_FILTER_SECTIONS.LISTINGS,
        label: listingsLabel,
        summary:
          formatTargetsSummary(filters.listingIds, listingGroups, {
            countLabel: (count) => t("settings.blackout.filter_listings_count", { defaultValue: "{{count}} listings", count })
          }) || t("settings.blackout.filter_listings_all", { defaultValue: "All listings" }),
        isActive: filters.listingIds.length > 0,
        onReset: () => onChange({ listingIds: [] }),
        panel: (
          <CheckboxFilterPanel
            hasFooter={false}
            variant={FILTER_PANEL_VARIANTS.INLINE}
            groups={listingGroups}
            selectedIds={filters.listingIds}
            onApply={(listingIds) => onChange({ listingIds })}
          />
        )
      }
    ]

    const drawerFooter = (
      <Flex gap="var(--spacing-s)" className={styles.sheetActions}>
        <Button block shape="round" onClick={onClear} disabled={!activeCount}>
          {t("settings.blackout.clear_all", { defaultValue: "Clear all" })}
        </Button>
        <Button block type="primary" shape="round" onClick={() => setIsDrawerOpen(false)}>
          {activeCount
            ? t("settings.blackout.show_results_count", { defaultValue: "Show results ({{count}})", count: activeCount })
            : t("settings.blackout.show_results", { defaultValue: "Show results" })}
        </Button>
      </Flex>
    )

    return (
      <Flex vertical gap="var(--spacing-s)">
        <button
          type="button"
          className={`${styles.pill} ${styles.mobileTrigger} ${activeCount ? styles.pillActive : ""}`}
          onClick={() => setIsDrawerOpen(true)}
        >
          <Icon name="Sliders" width={20} height={20} color="var(--color-primary)" />
          <span className={styles.mobileTriggerLabel}>{t("settings.blackout.filters", { defaultValue: "Filters" })}</span>
          {activeCount > 0 && <span className={styles.pillCount}>{activeCount}</span>}
        </button>

        {chips}

        <CustomDrawer
          hasHandle
          title={t("settings.blackout.filters", { defaultValue: "Filters" })}
          open={isDrawerOpen}
          setOpen={setIsDrawerOpen}
          footer={drawerFooter}
          className={styles.sheet}
        >
          <FilterAccordion sections={sections} />
        </CustomDrawer>
      </Flex>
    )
  }

  return (
    <Flex vertical gap="var(--spacing-s)">
      <Flex align="center" gap="var(--spacing-s)" className={styles.pills}>
        <FilterPill
          label={dateLabel}
          iconName="Calendar"
          count={hasDateRange ? 1 : 0}
          isOpen={openPanel === BLACKOUT_FILTER_SECTIONS.DATE}
          onOpenChange={togglePanel(BLACKOUT_FILTER_SECTIONS.DATE)}
        >
          <div className={styles.datePanel}>
            <DateRangeFilterPanel
              hasFooter
              variant={FILTER_PANEL_VARIANTS.INLINE}
              value={filters.dateRange}
              onChange={(dateRange) => onChange({ dateRange })}
              onClose={closePanel}
            />
          </div>
        </FilterPill>

        <FilterPill
          label={resourcesLabel}
          iconName="Package"
          count={filters.resourceIds.length}
          isOpen={openPanel === BLACKOUT_FILTER_SECTIONS.RESOURCES}
          onOpenChange={togglePanel(BLACKOUT_FILTER_SECTIONS.RESOURCES)}
        >
          <CheckboxFilterPanel groups={resourceGroups} selectedIds={filters.resourceIds} onApply={(resourceIds) => onChange({ resourceIds })} onClose={closePanel} />
        </FilterPill>

        <FilterPill
          label={listingsLabel}
          count={filters.listingIds.length}
          isOpen={openPanel === BLACKOUT_FILTER_SECTIONS.LISTINGS}
          onOpenChange={togglePanel(BLACKOUT_FILTER_SECTIONS.LISTINGS)}
        >
          <CheckboxFilterPanel groups={listingGroups} selectedIds={filters.listingIds} onApply={(listingIds) => onChange({ listingIds })} onClose={closePanel} />
        </FilterPill>
      </Flex>

      {chips}
    </Flex>
  )
}

const groupsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string,
    iconName: PropTypes.string,
    options: PropTypes.array.isRequired
  })
)

BlackoutFilters.propTypes = {
  filters: PropTypes.shape({
    dateRange: PropTypes.array,
    resourceIds: PropTypes.array.isRequired,
    listingIds: PropTypes.array.isRequired
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  resourceGroups: groupsPropType.isRequired,
  listingGroups: groupsPropType.isRequired
}

export default BlackoutFilters
